import { supabaseClient } from "@/lib/supabase";
import type { Json } from "@/types/database/database";

export const SKIPPED_PHONE_META_KEY = "skipped_phone";
export const SKIPPED_EMAIL_META_KEY = "skipped_email";

export type OnboardingContactSkipField = "phone" | "email";

export type UserMetaData = Record<string, unknown> | null | undefined;

function asMetaRecord(meta: UserMetaData | Json): Record<string, unknown> {
    if (!meta || typeof meta !== "object" || Array.isArray(meta)) return {};
    return meta as Record<string, unknown>;
}

/** Skip = do not hard-redirect into the wizard again. Does not mark the field complete. */
export function isOnboardingPhoneSkipped(meta: UserMetaData | Json): boolean {
    return asMetaRecord(meta)[SKIPPED_PHONE_META_KEY] === true;
}

export function isOnboardingEmailSkipped(meta: UserMetaData | Json): boolean {
    return asMetaRecord(meta)[SKIPPED_EMAIL_META_KEY] === true;
}

function metaKeyForField(field: OnboardingContactSkipField): string {
    return field === "phone" ? SKIPPED_PHONE_META_KEY : SKIPPED_EMAIL_META_KEY;
}

/** Merge a skip flag into users.user_meta_data (does not write a fake phone/email). */
export async function persistOnboardingContactSkip(
    userId: string,
    field: OnboardingContactSkipField
): Promise<Record<string, unknown>> {
    const key = metaKeyForField(field);
    const { data: existing, error: readError } = await supabaseClient
        .from("users")
        .select("user_meta_data")
        .eq("user_id", userId)
        .maybeSingle();

    if (readError) throw readError;

    const nextMeta = { ...asMetaRecord(existing?.user_meta_data), [key]: true };
    const { error: writeError } = await supabaseClient
        .from("users")
        .update({ user_meta_data: nextMeta })
        .eq("user_id", userId);

    if (writeError) throw writeError;
    return nextMeta;
}

/** Clear skip flag once a real phone/email is saved. */
export async function clearOnboardingContactSkip(
    userId: string,
    field: OnboardingContactSkipField
): Promise<Record<string, unknown>> {
    const key = metaKeyForField(field);
    const { data: existing, error: readError } = await supabaseClient
        .from("users")
        .select("user_meta_data")
        .eq("user_id", userId)
        .maybeSingle();

    if (readError) throw readError;

    const current = asMetaRecord(existing?.user_meta_data);
    if (!(key in current)) return current;

    const nextMeta = { ...current };
    delete nextMeta[key];
    const { error: writeError } = await supabaseClient
        .from("users")
        .update({ user_meta_data: nextMeta })
        .eq("user_id", userId);

    if (writeError) throw writeError;
    return nextMeta;
}
